import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Card, Text } from '@fragments';
import ComposerDecision from './components/ComposerDecision';
import ComposerSuggestions from './components/ComposerSuggestions';
import ComposerInput from './components/ComposerInput';
import FormatSelector from './components/FormatSelector';
import HandleInput from './components/HandleInput';
import AccountConfirm from './components/AccountConfirm';
import './components/HandleInput/styles.scss';
import OnboardingReply from './components/OnboardingReply';
import useComposer from './hooks/useComposer';
import memoStudio from '../../utils/memoStudio';
import './styles.scss';

// One surface for every way in: the onboarding (@ → account → answers), the
// prompt, and the run's pending decisions. The content swaps inside the morph,
// which animates the height (and narrows into a picker for options), so each
// step reads as the same card changing.
function Composer({ studio: s, compact = false }) {
  const { action, flow, view, setView, surface, picker, height, reduced, contentRef, draft, value, setValue, submit, showTopics, showRefine, needsFormat } = useComposer(s, compact);
  // The @ row stands alone: no shared surface around it.
  const bare = ['loading', 'handle', 'confirm'].includes(flow);
  const content = action ? <ComposerDecision studio={s} action={action} view={view} setView={setView} />
    : flow === 'loading' ? <div className="studio-handle-field"><div className="studio-handle"><Text.p contrast={6} loading>{s.t('handlePlaceholder')}</Text.p></div></div>
    : flow === 'handle' ? <HandleInput studio={s} />
    : flow === 'confirm' ? <AccountConfirm studio={s} />
    : flow ? <OnboardingReply studio={s} view={view} setView={setView} />
    : <>
      {needsFormat && <FormatSelector studio={s} initial />}
      {(showRefine || showTopics) && <ComposerSuggestions studio={s} compact={compact} showTopics={showTopics} setValue={setValue} />}
      <ComposerInput studio={s} compact={compact} draft={draft} value={value} setValue={setValue} submit={submit} disabled={needsFormat} showFormat={!needsFormat} />
    </>;
  return <div className={`studio-composer-wrap ${compact ? 'is-compact' : ''} ${picker ? 'is-picker' : ''}`}>
    <Card className={`studio-prompt-surface ${bare ? 'is-bare' : ''}`} noBodyPadding flat>
      {showTopics && <div className="studio-topics-aura" aria-hidden="true" />}
      <motion.div className="studio-composer-morph" initial={false} animate={{ height: height ?? 'auto' }} transition={{ duration: reduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div key={surface} ref={contentRef} initial={{ opacity: 0, y: reduced ? 0 : 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduced ? 0 : -3 }} transition={{ duration: reduced ? 0 : 0.12 }}>
            {content}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </Card>
  </div>;
}

// Every `studio` field the composer tree reads — useComposer, ComposerInput (useVoiceDraft),
// ComposerSuggestions (TopicPreview), FormatSelector, PersonalityButton, ComposerDecision (StoryForm),
// HandleInput, AccountConfirm, OnboardingReply. Add a key whenever a read is added.
export default memoStudio(Composer, ['t', 'locale', 'auth', 'entry', 'updateEntry', 'formatLocked', 'current', 'run', 'active', 'revision', 'replyDraft', 'setReply',
  'send', 'create', 'onboarding', 'features', 'topics', 'topic', 'chooseTopic', 'clearTopic', 'navigate', 'snapshot.memories', 'respond', 'cancelRun', 'storySubmit', 'transcribe',
  'setModal', 'setError', 'goToLogin', 'attachVoice', 'discardVoice', 'voice', 'error', 'profile', 'myPersonality',
  // Arts (2.7.0 proposal): the formats on offer, the art's scope and suggestions, the home's art choices.
  'capabilities', 'art', 'clearArtTemplate', 'setArtRatio',
  // Images attached to the prompt (ComposerInput, ComposerAttachments).
  'attachments',
  // The art's image question (ComposerDecision, 2.9.0): its skip; "Escolher imagens" opens the dialog through setModal.
  'skipImages']);
